/**
 * Reverse geocoding via OSM Nominatim (no API key).
 * Respect usage policy: throttle, cache; for production load use self-hosted Nominatim or a proxy.
 * @see https://operations.osmfoundation.org/policies/nominatim/
 */
const addressCache = new Map();
const inflightByKey = new Map();
const blockedUntilByKey = new Map();
const geocodeQueue = [];
let geocodeBusy = false;

const NOMINATIM_THROTTLE_MS = 1100;
const GEOCODE_MAX_RETRY = 3;
const FAILURE_COOLDOWN_MS = 5 * 60 * 1000;

function fallbackCoords(lat, lng) {
  return `${Number(lat).toFixed(5)}, ${Number(lng).toFixed(5)}`;
}

function nominatimReverse(lat, lng) {
  const base =
    process.env.REACT_APP_NOMINATIM_URL ||
    'https://nominatim.openstreetmap.org/reverse';
  const u = new URL(base);
  u.searchParams.set('format', 'jsonv2');
  u.searchParams.set('lat', String(lat));
  u.searchParams.set('lon', String(lng));
  if (process.env.REACT_APP_NOMINATIM_EMAIL) {
    u.searchParams.set('email', process.env.REACT_APP_NOMINATIM_EMAIL);
  }
  return fetch(u.toString(), {
    headers: {
      Accept: 'application/json',
      'Accept-Language': 'en',
    },
  }).then((r) => {
    if (r.status === 429) {
      throw new Error('RATE_LIMIT');
    }
    if (!r.ok) throw new Error(`HTTP_${r.status}`);
    return r.json();
  });
}

function processGeocodeQueue() {
  if (geocodeBusy || geocodeQueue.length === 0) return;
  geocodeBusy = true;
  const { lat, lng, key, resolve, retryCount = 0 } = geocodeQueue.shift();

  if (addressCache.has(key)) {
    resolve(addressCache.get(key));
    geocodeBusy = false;
    setTimeout(processGeocodeQueue, 0);
    return;
  }

  const done = (text) => {
    addressCache.set(key, text);
    resolve(text);
    geocodeBusy = false;
    setTimeout(processGeocodeQueue, NOMINATIM_THROTTLE_MS);
  };

  nominatimReverse(lat, lng)
    .then((data) => {
      const text =
        (data && (data.display_name || data.name)) ||
        `${Number(lat).toFixed(5)}, ${Number(lng).toFixed(5)}`;
      done(String(text));
    })
    .catch(() => {
      if (retryCount < GEOCODE_MAX_RETRY) {
        geocodeBusy = false;
        geocodeQueue.unshift({
          lat,
          lng,
          key,
          resolve,
          retryCount: retryCount + 1,
        });
        setTimeout(
          processGeocodeQueue,
          NOMINATIM_THROTTLE_MS * (retryCount + 2)
        );
        return;
      }
      blockedUntilByKey.set(key, Date.now() + FAILURE_COOLDOWN_MS);
      done(fallbackCoords(lat, lng));
    });
}

/**
 * Reverse-geocode a coordinate to a human-readable address string.
 * Falls back to a "lat, lng" text when remote geocoding is unavailable.
 *
 * @param {number} lat
 * @param {number} lng
 * @returns {Promise<string>}
 */
export function enqueueGeocode(lat, lng) {
  const key = `${Number(lat).toFixed(4)},${Number(lng).toFixed(4)}`;
  const now = Date.now();
  if (addressCache.has(key)) {
    return Promise.resolve(String(addressCache.get(key)));
  }
  if (inflightByKey.has(key)) {
    return inflightByKey.get(key);
  }
  if ((blockedUntilByKey.get(key) || 0) > now) {
    return Promise.resolve(fallbackCoords(lat, lng));
  }
  /** @type {Promise<string>} */
  const req = new Promise((resolve) => {
    geocodeQueue.push({ lat, lng, key, resolve, retryCount: 0 });
    processGeocodeQueue();
  }).finally(() => {
    inflightByKey.delete(key);
  });
  inflightByKey.set(key, req);
  return req;
}
